import { kioskLocation } from "../App/kioskRoutes";
import { React, useEffect, useState, useRef, useCallback } from "react";
import Backend from "../utils/api.js";
import { withRouter } from "react-router-dom";
import { useKioskToast } from "../Components/KioskToast.jsx";
import {
  Flex,
  Button,
  Box,
  Text,
  SimpleGrid,
} from "@chakra-ui/react";
import { useDocumentTitle } from "../utils/useDocumentTitle";

import { useHistory } from "react-router-dom";
import ProfileAppBar from "../User/Components/ProfileAppBar.jsx";
import KioskActionBar from "../Components/KioskActionBar.jsx";
import PaginationArrow from "../Components/PaginationArrow.jsx";
import ToolCard from "../User/Components/ToolCard.jsx";
import PageIndicator from "../Components/PageIndicator.jsx";

const CARDS_PER_PAGE = 12;

function CustomReasons({ user, lab }) {
  const history = useHistory();
  const { show: showToast, toastElements } = useKioskToast();
  const [selectedReason, setSelectedReason] = useState(null);
  const [skipToolSelection, setSkipToolSelection] = useState(Boolean(lab?.skipToolSelection));
  const [currentPage, setCurrentPage] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [completionUncertain, setCompletionUncertain] = useState(false);
  const draftRef = useRef(null);
  const submittingRef = useRef(false);
  const [reasonOptions, setReasonOptions] = useState([]);
  const [skipCheckInPurpose, setSkipCheckInPurpose] = useState(false);
  const [purposeStatus, setPurposeStatus] = useState('loading');
  const [purposeLoadAttempt, setPurposeLoadAttempt] = useState(0);
  const automaticAttemptRef = useRef(false);
  const noPurposes = purposeStatus === 'ready' && (skipCheckInPurpose || reasonOptions.length === 0);

  useDocumentTitle("Select a check-in purpose – Pinpoint");

  useEffect(() => {
    let active = true;
    automaticAttemptRef.current = false;
    setPurposeStatus('loading');
    const load = async () => {
      const space = await Backend.getLabById({ id: lab.id, UID: user.UID });
      if (!active) return;
      if (space.status !== 200) throw new Error('settings');
      setSkipToolSelection(Boolean(space.skipToolSelection));
      setSkipCheckInPurpose(Boolean(space.skipCheckInPurpose));
      if (space.skipCheckInPurpose) {
        setReasonOptions([]);
      } else {
        const res = await Backend.getAllReasons(user.UID, lab.id);
        if (!active) return;
        if (res?.status !== 200 || !Array.isArray(res.items)) throw new Error('purposes');
        setReasonOptions(res.items.filter(reason => !reason.isDeleted));
      }
      setPurposeStatus('ready');
    };
    load().catch(() => { if (active) setPurposeStatus('error'); });
    return () => { active = false; };
  }, [lab.id, user.UID, purposeLoadAttempt]);

  const totalPages = Math.ceil(reasonOptions.length / CARDS_PER_PAGE);
  const currentPageReasons = reasonOptions.slice(
    currentPage * CARDS_PER_PAGE,
    (currentPage + 1) * CARDS_PER_PAGE
  );

  const handleSubmit = useCallback(async () => {
    if ((!selectedReason && !noPurposes) || submittingRef.current) return;
    submittingRef.current = true;
    setIsSubmitting(true);
    try {
      const space = await Backend.getLabById({ id: lab.id, UID: user.UID });
      if (space.status !== 200) throw new Error('settings');
      let omitPurpose = noPurposes || space.skipCheckInPurpose;
      const purposeKey = omitPurpose ? "no-purpose" : `${selectedReason.id}:${selectedReason.time_interval}`;
      if (!draftRef.current || (!completionUncertain && draftRef.current.purposeKey !== purposeKey)) {
        const prepared = await Backend.prepareCheckIn({
          UID: user.UID, userSwipeNum: user.swipeNum ?? 0, lab: lab.id,
          ...(omitPurpose ? {} : { reason: selectedReason.custom_reason, intervalMinutes: selectedReason.time_interval, reasonId: selectedReason.id }),
        });
        if (prepared.error === 'purposes_available') {
          draftRef.current = null;
          setSelectedReason(null);
          setPurposeLoadAttempt(value => value + 1);
          return;
        }
        if (prepared.status !== 200 || !prepared.draft_token) throw new Error('draft');
        draftRef.current = { purposeKey, token: prepared.draft_token };
        space.skipToolSelection = prepared.skip_tool_selection ?? space.skipToolSelection;
        omitPurpose = prepared.no_purposes ?? omitPurpose;
      }
      // An uncertain direct completion must retry the same draft, even if the
      // admin setting changed in the meantime.
      if (space.skipToolSelection || completionUncertain) {
        const result = await Backend.completeCheckIn({ draftToken: draftRef.current.token, machineIds: [] });
        if (result.status === 200 || result.status === 201) {
          history.replace(kioskLocation('/kiosk/check-in/complete', history.location), { toolUsageFlow: 'check-in', usageSaved: false });
        } else if (result.error === 'purposes_available') {
          draftRef.current = null;
          setCompletionUncertain(false);
          setSelectedReason(null);
          setPurposeLoadAttempt(value => value + 1);
        } else {
          setCompletionUncertain(previous => previous || result.status === 0);
          showToast('error', result.status === 0 || completionUncertain
            ? 'Could not confirm whether your check-in completed. Please retry before leaving this screen.'
            : result.message || 'Could not complete your check-in. Please try again.');
        }
      } else {
        // A draft records no visit. Cancel on tool selection simply discards it.
        history.push(kioskLocation('/kiosk/check-in/tools', history.location), { reason: omitPurpose ? '' : selectedReason?.custom_reason ?? '',
          toolUsageFlow: 'check-in', draftToken: draftRef.current.token, ...(omitPurpose ? { noPurposes: true } : {}) });
      }
    } catch (error) {
      // API adapters return status 0 for transport failures; rejected completion
      // promises are treated conservatively by the same retry contract.
      if (draftRef.current && error.message !== 'settings' && error.message !== 'draft') {
        setCompletionUncertain(true);
        showToast('error', 'Could not confirm whether your check-in completed. Please retry before leaving this screen.');
      } else {
        showToast('error', 'Could not prepare your check-in. Please try again or contact space staff.');
      }
    } finally {
      submittingRef.current = false;
      setIsSubmitting(false);
    }
  }, [selectedReason, noPurposes, lab.id, user.UID, user.swipeNum, completionUncertain, history, showToast]);

  // Only loaded settings or a successful empty response can advance automatically. A failed
  // completion stays on this screen for an explicit retry with the same draft.
  useEffect(() => {
    if (noPurposes && !automaticAttemptRef.current) {
      automaticAttemptRef.current = true;
      handleSubmit();
    }
  }, [noPurposes, handleSubmit]);

  return (
    <Flex direction="column" minH="100vh" bg="gray.50" sx={{ touchAction: "manipulation" }}>
      <ProfileAppBar
        user={user}
        signoutLocation="/kiosk"
        cancelPending={isSubmitting || completionUncertain}
        title={`Welcome, ${user.firstName}!`}
        subtitle={noPurposes ? "Continuing check-in…" : "Why are you visiting this space today?"}
      />
      <Flex flex={1} direction="column" px={8} maxW="container.xl" mx="auto" w="full" justify="center">

        {purposeStatus !== 'ready' || noPurposes ? (
          <Text fontSize="xl" color="gray.600" textAlign="center" role={purposeStatus === 'error' ? 'alert' : 'status'}>
            {purposeStatus === 'loading' ? 'Loading check-in purposes…' : purposeStatus === 'error'
              ? 'Check-in settings or purposes could not be loaded. Please try again.'
              : isSubmitting ? (skipToolSelection ? 'Completing check-in…' : 'Continuing check-in…') : 'Please retry to continue check-in.'}
          </Text>
        ) : (
          <Flex w="full" align="center" gap={4}>
            {totalPages > 1 && (
              <PaginationArrow
                direction="left"
                disabled={currentPage === 0}
                onClick={() => setCurrentPage(p => p - 1)}
              />
            )}

            {/* Reason cards grid */}
            <Box flex={1} minW={0}>
              <SimpleGrid columns={{ base: 1, md: 2, lg: 3, xl: 4 }} spacing={4} w="full" role="radiogroup" aria-label="Check-in purpose">
                {currentPageReasons.map((option) => (
                  <ToolCard
                    key={option.id || option.custom_reason}
                    name={option.custom_reason}
                    isReadOnly={isSubmitting || completionUncertain}
                    accessibleLabel="Check-in purpose"
                    isSelected={selectedReason?.id === option.id}
                    onClick={() => setSelectedReason(option)}
                  />
                ))}
              </SimpleGrid>
            </Box>

            {totalPages > 1 && (
              <PaginationArrow
                direction="right"
                disabled={currentPage >= totalPages - 1}
                onClick={() => setCurrentPage(p => p + 1)}
              />
            )}
          </Flex>
        )}

      </Flex>

      <PageIndicator currentPage={currentPage} totalPages={totalPages} />

      <KioskActionBar>
        <Button
          size="kiosk"
          w="full"
          isDisabled={purposeStatus === 'loading' || (purposeStatus === 'ready' && !noPurposes && !selectedReason)}
          isLoading={isSubmitting || purposeStatus === 'loading'}
          loadingText={purposeStatus === "loading" ? "Loading purposes…" : skipToolSelection ? "Completing check-in…" : "Continuing…"}
          onClick={() => purposeStatus === 'error' ? setPurposeLoadAttempt(value => value + 1) : handleSubmit()}
        >
          {purposeStatus === "error" ? "Try again" : noPurposes ? "Retry check-in" : skipToolSelection ? "Complete check-in" : "Continue"}
        </Button>
      </KioskActionBar>
      {toastElements}
    </Flex>
  );
}

export default withRouter(CustomReasons);
